/**
 * Pre-seeded form templates for the form builder (item #9 Phase 3).
 *
 * Each template is a plain object the FormsManager can hand off to the
 * existing CRUD endpoints — first POST `/api/forms` with the metadata,
 * then one POST per question to `/api/forms/:id/questions`. No backend
 * surface is added; templates are just convenience UI on top of existing
 * routes.
 *
 * Templates intentionally span verticals (wellness, fitness, grooming,
 * salons, body-art, photography) to support the project's
 * generic-positioning roadmap. Admins clone, then edit freely — the
 * stored form has no link back to its source template.
 *
 * Question shape mirrors the API:
 *   { label, type, is_required?, placeholder?, help_text?, options? }
 *
 * `options` is required for type 'select' and 'checkbox'.
 */

export const FORM_TEMPLATES = [
  {
    key: 'par_q',
    name: 'PAR-Q (Physical Activity Readiness Questionnaire)',
    description: 'Standard pre-exercise health screening. Required before personal training, fitness assessments, or yoga programs.',
    intro_text: 'Regular physical activity is fun and healthy. For most people, increasing activity is very safe. Please answer the following questions honestly to help us safely tailor your program.',
    thank_you_text: 'Thank you. We\'ll review your answers before your first session and follow up if anything needs clarification.',
    questions: [
      { label: 'Has your doctor ever said you have a heart condition and that you should only do physical activity recommended by a doctor?', type: 'yes_no', is_required: true },
      { label: 'Do you feel pain in your chest when you do physical activity?', type: 'yes_no', is_required: true },
      { label: 'In the past month, have you had chest pain when you were not doing physical activity?', type: 'yes_no', is_required: true },
      { label: 'Do you lose your balance because of dizziness or do you ever lose consciousness?', type: 'yes_no', is_required: true },
      { label: 'Do you have a bone or joint problem that could be made worse by a change in your physical activity?', type: 'yes_no', is_required: true },
      { label: 'Is your doctor currently prescribing drugs for your blood pressure or heart condition?', type: 'yes_no', is_required: true },
      { label: 'Do you know of any other reason why you should not do physical activity?', type: 'yes_no', is_required: true },
      { label: 'If you answered YES to any of the above, please describe', type: 'textarea', is_required: false, placeholder: 'Provide details so we can adapt your program safely.' },
      { label: 'Signature', type: 'signature', is_required: true },
      { label: 'Date', type: 'date', is_required: true },
    ],
  },
  {
    key: 'vaccination_record',
    name: 'Vaccination Record Upload',
    description: 'Collects current vaccination records. Common for grooming, daycare, boarding, and animal-services bookings.',
    intro_text: 'Please upload a recent vaccination record so we can keep our facility safe for all visitors. Records typically come from your veterinarian as a PDF or photo.',
    thank_you_text: 'Thanks — we\'ll attach this to your account so you don\'t need to re-upload at your next visit.',
    questions: [
      { label: 'Pet / subject name', type: 'text', is_required: true, placeholder: 'e.g. Buddy' },
      { label: 'Vaccination record', type: 'file', is_required: true, help_text: 'Image or PDF. Most vets can email you a copy.' },
      { label: 'Date of most recent vaccinations', type: 'date', is_required: false },
      { label: 'Veterinarian / clinic name', type: 'text', is_required: false },
      { label: 'Any vaccine reactions or sensitivities to note?', type: 'textarea', is_required: false },
    ],
  },
  {
    key: 'allergy_intake',
    name: 'Allergy & Sensitivity Intake',
    description: 'Captures known allergies, sensitivities, and product preferences. Use for skincare, beauty, salon, and grooming services.',
    intro_text: 'Please share any allergies or product sensitivities so we can choose the right products for your session.',
    thank_you_text: 'Thanks — we\'ve flagged your file so the team is aware before you arrive.',
    questions: [
      { label: 'Do you have any known allergies?', type: 'yes_no', is_required: true },
      { label: 'List allergies and reaction severity', type: 'textarea', is_required: false, placeholder: 'e.g. tree nuts (anaphylactic), latex (rash)' },
      { label: 'Common ingredient sensitivities', type: 'checkbox', is_required: false, options: ['Fragrance / parfum', 'Sulfates', 'Parabens', 'Essential oils', 'Nickel', 'Latex', 'Adhesives', 'None of the above'] },
      { label: 'Are you currently taking any medications?', type: 'yes_no', is_required: false },
      { label: 'Medications and dosages (if comfortable sharing)', type: 'textarea', is_required: false },
      { label: 'Have you had a bad reaction to a similar service before?', type: 'yes_no', is_required: true },
      { label: 'Describe any past reactions', type: 'textarea', is_required: false },
    ],
  },
  {
    key: 'pet_intake',
    name: 'New Pet / Subject Intake',
    description: 'First-visit profile for grooming, training, daycare, or veterinary practices. Captures temperament, history, and special handling notes.',
    intro_text: 'Welcome! Please tell us about your pet so we can give them the best possible visit.',
    thank_you_text: 'Thanks. We\'re looking forward to meeting them.',
    questions: [
      { label: 'Pet name', type: 'text', is_required: true },
      { label: 'Breed', type: 'text', is_required: false },
      { label: 'Age', type: 'text', is_required: false, placeholder: 'e.g. 3 years' },
      { label: 'Weight', type: 'text', is_required: false },
      { label: 'Spayed / neutered?', type: 'yes_no', is_required: false },
      { label: 'How would you describe their temperament?', type: 'select', is_required: true, options: ['Easy-going', 'Shy / nervous', 'Excitable', 'Reactive to strangers', 'Reactive to other animals', 'Mixed / depends on situation'] },
      { label: 'Known triggers or fears', type: 'textarea', is_required: false, placeholder: 'e.g. dryers, nail clippers, men in hats' },
      { label: 'Has your pet ever bitten or attempted to bite a handler?', type: 'yes_no', is_required: true },
      { label: 'Current medications or medical conditions', type: 'textarea', is_required: false },
      { label: 'Veterinarian name and phone (for emergencies)', type: 'text', is_required: false },
      { label: 'Anything else we should know?', type: 'textarea', is_required: false },
    ],
  },
  {
    key: 'photo_release',
    name: 'Photography & Media Release',
    description: 'Permission to use photos / video for marketing or social media. Pair with photo-shoot, grooming, fitness, or event services.',
    intro_text: 'We sometimes share photos and videos of our work on our website and social media. Your participation is optional and you can change your mind at any time.',
    thank_you_text: 'Thanks for your response. Your preferences are now on file.',
    questions: [
      { label: 'Do you consent to photos being taken during your session?', type: 'yes_no', is_required: true },
      { label: 'May we use your photos on our website?', type: 'yes_no', is_required: true },
      { label: 'May we use your photos on social media (Instagram, Facebook, TikTok, etc.)?', type: 'yes_no', is_required: true },
      { label: 'May we tag you in social media posts?', type: 'yes_no', is_required: false },
      { label: 'Preferred display name / handle (if tagging)', type: 'text', is_required: false },
      { label: 'Any restrictions or notes', type: 'textarea', is_required: false, placeholder: 'e.g. faces blurred, no full-body shots' },
      { label: 'Signature', type: 'signature', is_required: true },
      { label: 'Date', type: 'date', is_required: true },
    ],
  },
  {
    key: 'tattoo_consent',
    name: 'Tattoo / Body Art Consent',
    description: 'Pre-procedure health screening, age verification, and informed consent for tattoo, piercing, microblading, and similar services.',
    intro_text: 'Please complete this consent form before your appointment. We require this for everyone\'s safety and to comply with applicable health regulations.',
    thank_you_text: 'Thanks — we\'ll review before your appointment. Bring valid photo ID on the day.',
    questions: [
      { label: 'Full legal name', type: 'text', is_required: true },
      { label: 'Date of birth', type: 'date', is_required: true },
      { label: 'I confirm I am 18 years of age or older (or have valid guardian consent on file)', type: 'yes_no', is_required: true },
      { label: 'Have you eaten in the last 4 hours?', type: 'yes_no', is_required: true, help_text: 'Eating before your appointment helps prevent fainting.' },
      { label: 'Are you pregnant or breastfeeding?', type: 'yes_no', is_required: true },
      { label: 'Do you have any of the following conditions?', type: 'checkbox', is_required: false, options: ['Diabetes', 'Hemophilia / blood clotting disorder', 'Heart condition', 'Epilepsy / seizures', 'Skin condition (eczema, psoriasis, keloids)', 'Hepatitis', 'HIV / AIDS', 'Active infection', 'None of the above'] },
      { label: 'Are you currently taking blood thinners (aspirin, warfarin, etc.)?', type: 'yes_no', is_required: true },
      { label: 'Any allergies to latex, nickel, dyes, or topical anaesthetics?', type: 'textarea', is_required: false },
      { label: 'Have you consumed alcohol or recreational drugs in the last 24 hours?', type: 'yes_no', is_required: true },
      { label: 'I have read and understood the aftercare instructions', type: 'yes_no', is_required: true },
      { label: 'I consent to the procedure described in my booking and understand the associated risks', type: 'yes_no', is_required: true },
      { label: 'Signature', type: 'signature', is_required: true },
      { label: 'Date', type: 'date', is_required: true },
    ],
  },
  {
    key: 'generic_intake',
    name: 'Generic Service Intake',
    description: 'Minimal blank-slate intake for any service. A starting point you can edit freely.',
    intro_text: 'Please answer a few quick questions before your appointment.',
    thank_you_text: 'Thanks — we\'ve received your responses.',
    questions: [
      { label: 'Full name', type: 'text', is_required: true },
      { label: 'Email', type: 'text', is_required: true },
      { label: 'Phone', type: 'text', is_required: false },
      { label: 'Is this your first visit with us?', type: 'yes_no', is_required: false },
      { label: 'How did you hear about us?', type: 'select', is_required: false, options: ['Google search', 'Social media', 'Friend / family', 'Walk-in', 'Other'] },
      { label: 'Anything we should know before your appointment?', type: 'textarea', is_required: false },
    ],
  },
];
